import { PopulatedDatabaseQuestion } from '../types/types';
import api from './config';

const LLM_API_URL = `${process.env.REACT_APP_SERVER_URL}/llm`;

/**
 * Generates a summary of answers for a given question using the LLM service.
 *
 * @param question - The question with populated answers to summarize
 * @returns Promise resolving to the generated summary
 * @throws Error if the request fails or returns a non-200 status
 */
export const generateLLMSummary = async (question: PopulatedDatabaseQuestion): Promise<string> => {
  const res = await api.post(`${LLM_API_URL}/generate-summary`, { question });
  if (res.status !== 200) {
    throw new Error('Error while generating summary');
  }
  return res.data.summary;
};

/**
 * Generates an AI solution for a given question using the LLM service.
 * @param question - The question text to generate a solution for
 * @returns Promise resolving to the generated solution
 * @throws Error if the request fails or returns a non-200 status
 */
export const generateAISolution = async (question: string): Promise<string> => {
  const res = await api.post(`${LLM_API_URL}/generate-answer`, {
    question,
    context: { type: 'solution' },
  });
  if (res.status !== 200) {
    throw new Error('Error while generating AI solution');
  }
  return res.data.answer;
};

interface Message {
  role: 'system' | 'user' | 'assistant';
  content: string;
}

export const generateFollowUpResponse = async (
  question: string,
  previousResponse: string,
  followUp: string,
): Promise<string> => {
  const messages: Message[] = [
    {
      role: 'system',
      content:
        'You are a helpful technical assistant. Provide clear, concise answers to follow-up questions.',
    },
    {
      role: 'user',
      content: `Original Question: ${question}`,
    },
    {
      role: 'assistant',
      content: previousResponse,
    },
    {
      role: 'user',
      content: followUp,
    },
  ];

  const response = await fetch(`${LLM_API_URL}/chat`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ messages }),
  });

  if (!response.ok) {
    throw new Error('Failed to generate follow-up response');
  }

  const data = await response.json();
  if (!data.success || !data.reply) {
    throw new Error(data.error || 'Failed to generate response');
  }

  return data.reply;
};

/**
 * Moderates a question's content using the LLM service.
 * @param question - The question text to moderate
 * @returns Promise resolving to the moderation result
 * @throws Error if the request fails or returns a non-200 status
 */
export const moderateQuestion = async (
  question: string,
): Promise<{ success: boolean; message: string }> => {
  const res = await api.post(`${LLM_API_URL}/moderate`, { question });
  if (res.status !== 200) {
    throw new Error('Error while moderating content');
  }
  return res.data;
};

/**
 * Finds similar questions and returns both the most similar question and average similarity score
 * @param query - The question text to find similar questions for
 * @returns Promise resolving to an object containing the most similar question and average similarity score
 * @throws Error if the request fails or returns a non-200 status
 */
export const findSimilarQuestions = async (
  query: string,
): Promise<{ similarQuestion: PopulatedDatabaseQuestion | null; averageSimilarity: number }> => {
  const res = await api.post(`${LLM_API_URL}/check-similarity`, { question: query });
  if (res.status !== 200) {
    throw new Error('Error while finding similar questions');
  }
  return res.data;
};

/**
 * Fetches AI moderation logs from the server
 * @returns Promise resolving to an array of moderation logs
 * @throws Error if the request fails or returns a non-200 status
 */
export const getModerationLogs = async (): Promise<
  Array<{
    id: string;
    question: string;
    result: boolean;
    message: string;
    timestamp: Date;
  }>
> => {
  const res = await api.get(`${LLM_API_URL}/moderation-logs`);
  if (res.status !== 200) {
    throw new Error('Error while fetching moderation logs');
  }
  return res.data;
};
